import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

const ServerSettings: React.FC = () => {
    const { t } = useTranslation();
    const [domain, setDomain] = useState('');
    const [isDomainOnline, setIsDomainOnline] = useState<boolean | null>(null);
    const [isCheckingDomain, setIsCheckingDomain] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const { getDomain, setDomain: updateDomain, checkDomainStatus } = useAuth();

    useEffect(() => {
        setDomain(getDomain());
        checkCurrentDomainStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkCurrentDomainStatus = async () => {
        setIsCheckingDomain(true);
        try {
            const isOnline = await checkDomainStatus();
            setIsDomainOnline(isOnline);
        } catch (err) {
            setIsDomainOnline(false);
        } finally {
            setIsCheckingDomain(false);
        }
    };

    const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDomain(e.target.value);
        setError('');
        setSuccess('');
    };

    const handleSaveDomain = async () => {
        if (!domain.trim()) {
            setError(t('serverSettings.errors.emptyDomain'));
            return;
        }

        setIsCheckingDomain(true);
        setError('');
        setSuccess('');

        try {
            await updateDomain(domain.trim());
            const isOnline = await checkDomainStatus();
            setIsDomainOnline(isOnline);
            setSuccess(t('serverSettings.success.saved'));
        } catch (err: any) {
            setError(err instanceof Error ? err.message : t('serverSettings.errors.saveFailed'));
            setIsDomainOnline(false);
        } finally {
            setIsCheckingDomain(false);
        }
    };

    const handleReset = () => {
        setDomain('https://server1.notizy.nl');
        setError('');
        setSuccess('');
    };

    return (
        <div className="space-y-6">
            <div>
                <label htmlFor="domain" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('serverSettings.domainLabel')}
                </label>
                <input
                    id="domain"
                    type="text"
                    value={domain}
                    onChange={handleDomainChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-600 focus:border-primary-600"
                    placeholder="https://server1.notizy.nl"
                />
                <p className="mt-1 text-sm text-gray-500">
                    {t('serverSettings.domainHelp')}
                </p>
            </div>

            {isDomainOnline !== null && (
                <div className={`flex items-center p-2 rounded-md text-sm ${isDomainOnline
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
                    }`}>
                    <div className={`w-2 h-2 rounded-full mr-2 ${isDomainOnline ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                    <span className="text-xs">
                        {isDomainOnline ? t('serverSettings.serverOnline') : t('serverSettings.serverOffline')}
                    </span>
                    {isCheckingDomain && (
                        <svg className="animate-spin ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    )}
                </div>
            )}

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            {success && (
                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                    <p className="text-sm text-green-600">{success}</p>
                </div>
            )}

            <div className="flex space-x-4">
                <button
                    onClick={handleSaveDomain}
                    disabled={isCheckingDomain}
                    className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isCheckingDomain ? (
                        <div className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {t('serverSettings.checking')}
                        </div>
                    ) : (
                        t('common.save')
                    )}
                </button>

                <button
                    onClick={handleReset}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                    {t('serverSettings.reset')}
                </button>

                <button
                    onClick={checkCurrentDomainStatus}
                    disabled={isCheckingDomain}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                    {t('serverSettings.test')}
                </button>
            </div>
        </div>
    );
};

export default ServerSettings;


